/**
 * Page Context and Provider
 *
 * This module provides React Context for managing Page state throughout the Auriga application.
 * It handles fetching available Page level data across grains, tracking the current Page in view,
 * and automatically updating the current Page based on route parameters.
 *
 */

import { DEFAULT_PAGE_DRIVERS, KPI_DIMENSION_CONFIG_MAP } from '@constants/page';
import { useDeckContext } from '@context/DeckContext';
import { useUserContext } from '@context/UserContext';
import {
  CodeOrDescription,
  DatasetSource,
  DenominatorLabelValues,
  GalaxyKPIDrivers,
  Page,
} from '@services/api/deckManagement/graphql/schema/API';
import { useGetPagesByPageName } from '@services/api/deckManagement/hooks/usePage';
import { changeEntryField } from '@utils/deckManagementEntityHelpers';
import {
  asGalaxyDrivers,
  generateDriverDisplayOptions,
  kpiDriversEmpty,
  readPageDrivers,
  readPageDriverSets,
} from '@utils/kpiDriverHelpers';
import { initializePageModifiableProperties } from '@utils/pageHelpers';
import { cloneDeep, isEmpty, isNil, some, uniq } from 'lodash';
import { createContext, ReactNode, use, useCallback, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { LockStatus } from 'types/DeckContext.types';
import { DecklineRowData, PartialKPIDrivers } from 'types/Page.types';
import {
  DriverDisplayOptionMap,
  PageContextType,
  PageModifiableProperties,
  PageProviderProps,
} from 'types/PageContext.types';
import { KpiDimension, PageLayoutOverrides } from 'types/PageSidePanel.types';

/**
 * Page Context
 *
 * React context for Page state management.
 */
export const PageContext = createContext<PageContextType>({
  availablePagesByName: [],
  availablePageGrains: [],
  currentCorePageName: null,
  currentGranularityPage: null,
  currentPageDriverOptions: null,
  updatePageDriverActiveState: () => {},
  setCurrentPageKPIDrivers: () => {},
  selectedGranularity: null,
  segmentsDisplayMode: CodeOrDescription.CODE,
  setSelectedGranularity: () => {},
  setSegmentsDisplayMode: () => {},
  pageRowData: [],
  setPageRowData: () => {},
  isLoading: false,
  isError: false,
  error: null,
  setDenominatorLabel: () => {},
  modifiedPageProperties: null,
  pageLockStatus: { isLocked: false },
  configuredDatasets: [],
  pageLayoutOverrides: {},
  setPageLayoutOverrides: () => {},
});

/**
 * Page Provider Component
 *
 * This component provides Page context to the application.
 * It manages the state of available Page Grains, the current Page selection, and all Pages associated with a page Name.
 *
 * @param {PageProviderProps} props - The component props for the Page Provider
 * @returns {JSX.Element} The Page provider component
 */
export const PageContextProvider = ({ children }: PageProviderProps): ReactNode => {
  const { currentDeckId } = useDeckContext();
  const { username } = useUserContext();
  // Get pageName from route parameters
  const { pageName: routePageName } = useParams<{ pageName: string }>();
  const pageName = decodeURIComponent(routePageName || '');

  // State for current page and error handling
  const [availablePagesByName, setAvailablePagesByName] = useState<Page[]>([]);
  const [availablePageGrains, setAvailablePageGrains] = useState<string[]>([]);
  const [currentCorePageName, setCurrentCorePageName] = useState<string | null>(null);
  const [currentGranularityPage, setCurrentGranularityPageState] = useState<Page | null>(null);
  // Per-dataset activate-prefill baseline: when a user re-activates a toggled-off driver, its value is
  // restored from this dataset's originally-loaded page drivers. Keyed by DatasetSource so each
  // dataset's edit surface prefills from its own baseline (Galaxy is the only editing surface today).
  const [currentOriginalPageKPIDrivers, setCurrentOriginalPageKPIDriversState] = useState<Partial<
    Record<DatasetSource, GalaxyKPIDrivers>
  > | null>(null);
  const [currentPageDriverOptions, setCurrentPageDriverOptionsState] = useState<Partial<
    Record<DatasetSource, DriverDisplayOptionMap>
  > | null>(null);
  const [segmentsDisplayMode, setSegmentsDisplayMode] = useState<CodeOrDescription>(CodeOrDescription.CODE);
  const [selectedGranularity, setSelectedGranularity] = useState<string | null>(null);
  const [pageError, setPageError] = useState<string | null>(null);
  const [pageRowData, setPageRowData] = useState<DecklineRowData[]>([]);
  const [modifiedPageProperties, setModifiedPageProperties] = useState<PageModifiableProperties | null>(null);
  // DEV_TEAM_ADMIN-only preview overrides pinned from the side panel's Admin tab; fed into getPageLayout.
  const [pageLayoutOverrides, setPageLayoutOverrides] = useState<PageLayoutOverrides>({});

  // Fetch associated Pages based on the current page's page_name
  const {
    data: pagesByName,
    isLoading: isPagesByNameLoading,
    isError: isPagesByNameError,
    error: pagesByNameError,
  } = useGetPagesByPageName({
    page_name: pageName,
    deck_id: currentDeckId, // To filter API results by loaded Deck
    options: {
      // Only fetch when we have a valid pageName and deckId
      enabled: !isNil(pageName) && !isEmpty(pageName) && !isNil(currentDeckId) && !isEmpty(currentDeckId),
    },
  });

  /**
   * Resets any page data as needed to restore an empty context state
   * @param {boolean} omitPageNameReset - Whether to omit resetting the page name
   *
   */
  const resetPageData = useCallback((omitPageNameReset: boolean = false) => {
    if (!omitPageNameReset) {
      setCurrentCorePageName(null);
    }
    setAvailablePagesByName([]);
    setAvailablePageGrains([]);
    setCurrentGranularityPageState(null);
    setCurrentPageDriverOptionsState(null);
    setSelectedGranularity(null);
    setModifiedPageProperties(null);
  }, []);

  /**
   * Effect to automatically set current Page based on a change in the Route parameter
   *
   * This effect runs when the pageName route parameter changes and attempts
   * to set the first available granularity Page accordingly.
   */
  useEffect(() => {
    if (!isNil(pageName) && !isEmpty(pageName)) {
      // Reset any old values that will get updated
      resetPageData(true);
      // Assign the new Page name as the core Page name
      setCurrentCorePageName(pageName);
    } else {
      // Clear all page data if no pageName in route
      resetPageData();
    }
    setPageError(null);
  }, [pageName, resetPageData]);

  /**
   * Effect to automatically update available Page data based on the current Page selection by a User
   *
   * This effect runs when the currentCorePageName changes and updates the available Pages, Page Grains or Page Types,
   * based on the fetched API data. It also updates the selected granularity if it has changed.
   */
  useEffect(() => {
    if (currentCorePageName) {
      if (pagesByName && pagesByName.length > 0) {
        // Updates PagesByName, AvailablePageGrains, and Granularity selection only when there is a match for the current Page selected
        const pagesMatchPageSelection = some(pagesByName, (page) => page.page_name?.startsWith(currentCorePageName));
        if (!pagesMatchPageSelection) {
          console.error('PagesByName does not match current page selection. Skipping stale data...');
        } else {
          setAvailablePagesByName(pagesByName);
          // Clear any previous errors when data loads successfully
          setPageError(null);

          // Additionally, update the page grains we account for and filter for current deck.
          // This also treats Types as Grains for the frontend, in case grain does not exist.
          const updatedPageGrains: string[] = pagesByName
            .filter((page) => (!isNil(page.page_grain) || !isNil(page.page_type)) && page.deck_id === currentDeckId)
            .map((page) => {
              if (!isEmpty(page.page_grain)) return page.page_grain as string;
              return page.page_type as string;
            })
            .sort();
          if (updatedPageGrains && updatedPageGrains.length > 0) {
            setAvailablePageGrains(uniq(updatedPageGrains));
            // Set the initial granularity to the first one available if they User does not have a Selected granularity,
            // or the current selected granularity does not match what is available
            if (!selectedGranularity || !updatedPageGrains.includes(selectedGranularity)) {
              setSelectedGranularity(updatedPageGrains[0]);
            }
          }
        }
      }
    }
  }, [currentCorePageName, pagesByName, selectedGranularity, currentDeckId]);

  /**
   * Updates Page Driver Options for a given dataset.
   * @param updates The changed values which need to be assigned to the dataset's KPIDrivers
   * @param decklineUpdateHandler - When provided, gets called with the previous state as well as the update object.
   *  Used for iterating through decklines and updating those values to reflect the page drivers.
   * @param datasetSource - Which dataset's page-driver set to edit. Defaults to GALAXY — the only dataset
   *  with a side-panel editing surface today (HCTR page drivers are edited in the Page Setup wizard). The
   *  parameter targets the right entry of the dataset-keyed options map / `page_drivers` so a future
   *  mixed-dataset editing surface edits its own dataset's set rather than clobbering Galaxy.
   */
  const setCurrentPageKPIDrivers = useCallback(
    (
      updates: PartialKPIDrivers,
      decklineUpdateHandler?: (prev: GalaxyKPIDrivers, updates: PartialKPIDrivers) => void,
      datasetSource: DatasetSource = DatasetSource.GALAXY,
    ) => {
      setModifiedPageProperties((prev) => {
        if (isNil(prev)) {
          return prev;
        }

        // Read the editing baseline for the target dataset. Driver internals are Galaxy-typed because
        // Galaxy is the only dataset with a page-driver editing surface today; the dataset parameter
        // selects WHICH set is read/written, not a new HCTR write path (that is Epic H).
        const prevDrivers = asGalaxyDrivers(readPageDrivers(prev.page_drivers, datasetSource)) ?? {
          __typename: 'GalaxyKPIDrivers',
        };
        const newDrivers = {
          ...prevDrivers,
          ...updates,
        } as GalaxyKPIDrivers;

        // When decklineUpdateHandler is provided, call it with the prev state and updates.
        decklineUpdateHandler?.(cloneDeep(prevDrivers as GalaxyKPIDrivers), updates);
        // Update only the target dataset's entry of the dataset-keyed options map.
        setCurrentPageDriverOptionsState((prevOptions) => ({
          ...prevOptions,
          [datasetSource]: generateDriverDisplayOptions(newDrivers, null, undefined, datasetSource),
        }));
        return changeEntryField(prev, 'page_drivers', { ...prev.page_drivers, [datasetSource]: newDrivers });
      });
    },
    [],
  );

  /**
   * Effect to initialize Page Drivers with defaults if none exist.
   *
   * Seeding targets the Galaxy set (side-panel page-driver editing is Galaxy-only; see
   * setCurrentPageKPIDrivers). Skip it when the page is already configured for a non-Galaxy dataset:
   * on an HCTR-only page the Galaxy set is legitimately empty, and injecting a default GALAXY entry
   * would wrongly widen the page's configured datasets — surfacing Galaxy Root KPIs/Variants in the
   * dataset-filtered pickers and defaulting new variants to GALAXY.
   */
  useEffect(() => {
    if (isNil(modifiedPageProperties)) return;
    const configuredForNonGalaxy = Object.keys(modifiedPageProperties.page_drivers).some(
      (ds) => ds !== DatasetSource.GALAXY,
    );
    if (
      !configuredForNonGalaxy &&
      kpiDriversEmpty(asGalaxyDrivers(readPageDrivers(modifiedPageProperties.page_drivers, DatasetSource.GALAXY)))
    ) {
      setCurrentPageKPIDrivers(DEFAULT_PAGE_DRIVERS);
    }
  }, [modifiedPageProperties, setCurrentPageKPIDrivers]);

  /**
   * Updates the Denominator Label for the Current Page.
   * @param newValue - The new value for the Denominator Label
   */
  const setDenominatorLabel = useCallback((newValue: DenominatorLabelValues | null) => {
    setModifiedPageProperties((prev) => {
      if (isNil(prev)) {
        return prev;
      }

      return changeEntryField(prev, 'denominator_label', newValue);
    });
  }, []);

  /**
   * Updates the value of `active` for a given kpiDimension (when the user toggled the <Switch> component). When
   * deactivating a switch, the associated value and hierarchy are reset to `null`.
   * @param kpiDimension - The kpiDimension to update
   * @param active - The new value of `active`
   * @param decklineUpdateHandler - When provided, gets called with the previous state as well as the update object.
   *  Used for iterating through decklines and updating those values to reflect the page drivers.
   * @param datasetSource - Which dataset's page-driver set to toggle. Defaults to GALAXY — the only dataset
   *  with a side-panel editing surface today. Selects the right entry of the options map / baseline /
   *  `page_drivers` so a future mixed-dataset surface toggles its own dataset's set.
   */
  const updatePageDriverActiveState = useCallback(
    (
      kpiDimension: KpiDimension,
      active: boolean,
      decklineUpdateHandler?: (prev: GalaxyKPIDrivers, updates: PartialKPIDrivers) => void,
      datasetSource: DatasetSource = DatasetSource.GALAXY,
    ) => {
      const dimensionConfig = KPI_DIMENSION_CONFIG_MAP[kpiDimension];
      if (!dimensionConfig) {
        return;
      }

      const { decklineDrivers } = dimensionConfig;
      if (!decklineDrivers) {
        return;
      }

      // This dataset's activate-prefill baseline (the originally-loaded page drivers for the dataset).
      const originalDrivers = currentOriginalPageKPIDrivers?.[datasetSource] ?? null;

      setCurrentPageDriverOptionsState((prev) => {
        // Operate on the target dataset's entry of the dataset-keyed options map.
        const newDriverOptions = { ...(prev?.[datasetSource] ?? {}) };
        if (newDriverOptions[kpiDimension]) {
          newDriverOptions[kpiDimension].active = active;

          // If deactivating a switch, we need to reset the values associated with the kpiDimension to `null`
          if (!active) {
            newDriverOptions[kpiDimension].value = null;
            newDriverOptions[kpiDimension].hierarchy = null;
          } else {
            // When activating, pre-populate with the original KPI Driver Data (if available).
            newDriverOptions[kpiDimension].value =
              originalDrivers?.[decklineDrivers.valueAccessor! as keyof GalaxyKPIDrivers] || null;
            if (decklineDrivers.hierarchyAccessor) {
              newDriverOptions[kpiDimension].hierarchy =
                originalDrivers?.[decklineDrivers.hierarchyAccessor as keyof GalaxyKPIDrivers] || null;
            }
          }

          // Keep currentPageKPIDrivers synchronized
          const resetDrivers: PartialKPIDrivers = {
            [decklineDrivers.valueAccessor!]: newDriverOptions[kpiDimension].value,
          };
          if (decklineDrivers?.hierarchyAccessor) {
            resetDrivers[decklineDrivers.hierarchyAccessor as keyof PartialKPIDrivers] =
              newDriverOptions[kpiDimension].hierarchy;
          }
          const prevDrivers = asGalaxyDrivers(readPageDrivers(modifiedPageProperties?.page_drivers, datasetSource));
          if (!isNil(prevDrivers)) {
            // Used to pre-populate Decklines with previous values from the Page Driver state.
            // This helps to not require the user to enter a value on every deckline.
            const decklineEntryUpdates: PartialKPIDrivers = {
              [decklineDrivers.valueAccessor!]: prevDrivers[decklineDrivers.valueAccessor! as keyof GalaxyKPIDrivers],
            };
            if (decklineDrivers.hierarchyAccessor) {
              decklineEntryUpdates[decklineDrivers.hierarchyAccessor as keyof PartialKPIDrivers] =
                prevDrivers[decklineDrivers.hierarchyAccessor as keyof GalaxyKPIDrivers];
            }

            setCurrentPageKPIDrivers({ ...prevDrivers, ...resetDrivers }, undefined, datasetSource);
            // When decklineUpdateHandler is provided, call it with the prev state and updates.
            decklineUpdateHandler?.(cloneDeep(prevDrivers), decklineEntryUpdates);
          }
        }
        return { ...prev, [datasetSource]: newDriverOptions };
      });
    },
    [currentOriginalPageKPIDrivers, modifiedPageProperties, setCurrentPageKPIDrivers],
  );

  /**
   * Set current Page data by Granularity
   *
   * Attempts to find and set the current Page by the User selected granularity. It searches
   * in the availablePagesByName array for the desired Page data
   *
   * This is the primary way to change which Page is being viewed/edited.
   * This function is idempotent - calling it multiple times with the same granularity selection
   * will not trigger additional API calls or unnecessary re-renders.
   *
   * @param {string} targetGranularity - The ID of the Page to set as current
   */
  const setCurrentGranularityPage = useCallback(
    (targetGranularity: string) => {
      setPageError(null);

      // Only update selectedGranularity Page if it is actually different (considers both Grain and Type--where grain did not exist)
      if (
        currentGranularityPage &&
        (currentGranularityPage.page_grain === targetGranularity ||
          currentGranularityPage.page_type === targetGranularity)
      ) {
        console.debug('Page with granularity already selected, skipping:', targetGranularity);
        return;
      }

      // Return granular page if found in availablePagesByName
      const foundPage = availablePagesByName.find((page) => {
        return (page.page_grain === targetGranularity || page.page_type === targetGranularity) && page.page_name;
      });

      if (foundPage) {
        const driverSets = readPageDriverSets(foundPage.page_drivers);
        const galaxyDrivers = asGalaxyDrivers(readPageDrivers(driverSets, DatasetSource.GALAXY));
        // The Galaxy editing baseline for activate-prefill. On a Galaxy-configured (or unconfigured)
        // page this is the Galaxy set, seeded with defaults when empty; HCTR-only pages have no Galaxy
        // baseline (null) — their drivers are edited in the Page Setup wizard.
        const galaxyBaseline = kpiDriversEmpty(galaxyDrivers) ? DEFAULT_PAGE_DRIVERS : galaxyDrivers!;

        // Build display options for EVERY configured dataset, keyed by DatasetSource, so the side panel
        // can serve each deckline/view the options for its own dataset rather than a Galaxy-only map.
        const driverOptionsByDataset = (Object.keys(driverSets) as DatasetSource[]).reduce<
          Partial<Record<DatasetSource, DriverDisplayOptionMap>>
        >((acc, dataset) => {
          // Galaxy reuses the editing baseline (defaults-seeded) to preserve current behavior; other
          // datasets build directly from their own `page_drivers` set.
          const drivers = dataset === DatasetSource.GALAXY ? galaxyBaseline : readPageDrivers(driverSets, dataset);
          acc[dataset] = generateDriverDisplayOptions(drivers, null, undefined, dataset);
          return acc;
        }, {});

        // Preserve prior behavior for an unconfigured page: still expose a Galaxy options entry seeded
        // from defaults so the side panel renders rather than showing an empty page-driver list.
        if (
          !driverOptionsByDataset[DatasetSource.GALAXY] &&
          !some(Object.keys(driverSets), (ds) => ds !== DatasetSource.GALAXY)
        ) {
          driverOptionsByDataset[DatasetSource.GALAXY] = generateDriverDisplayOptions(
            galaxyBaseline,
            null,
            undefined,
            DatasetSource.GALAXY,
          );
        }

        // Per-dataset activate-prefill baseline. Galaxy uses the defaults-seeded editing baseline;
        // other configured datasets use their own (Galaxy-shaped) set when present. Only datasets with
        // a Galaxy-shaped baseline get an entry (HCTR sets resolve to null today — no editing surface).
        const originalByDataset = (Object.keys(driverSets) as DatasetSource[]).reduce<
          Partial<Record<DatasetSource, GalaxyKPIDrivers>>
        >((acc, dataset) => {
          const baseline =
            dataset === DatasetSource.GALAXY ? galaxyBaseline : asGalaxyDrivers(readPageDrivers(driverSets, dataset));
          if (!isNil(baseline)) acc[dataset] = baseline;
          return acc;
        }, {});
        // Preserve prior behavior for an unconfigured page: seed a Galaxy baseline from defaults.
        if (
          !originalByDataset[DatasetSource.GALAXY] &&
          !some(Object.keys(driverSets), (ds) => ds !== DatasetSource.GALAXY)
        ) {
          originalByDataset[DatasetSource.GALAXY] = galaxyBaseline;
        }

        setCurrentGranularityPageState(foundPage);
        setCurrentOriginalPageKPIDriversState(originalByDataset);
        setCurrentPageDriverOptionsState(driverOptionsByDataset);
        setModifiedPageProperties(initializePageModifiableProperties(foundPage));
      } else if (!availablePagesByName.length && !isPagesByNameLoading) {
        // If no pages by name are available with the granularity, and we're not loading, set error
        setPageError(`Page data for granularity "${targetGranularity}" could not be found.`);
        setCurrentGranularityPageState(null);
        setCurrentPageDriverOptionsState(null);
        setCurrentOriginalPageKPIDriversState(null);
        setModifiedPageProperties(null);
      }
    },
    [availablePagesByName, currentGranularityPage, isPagesByNameLoading],
  );

  /**
   * Effect to automatically set correct Page data when selected granularity changes
   */
  useEffect(() => {
    if (selectedGranularity) {
      setCurrentGranularityPage(selectedGranularity);
    }
  }, [selectedGranularity, setCurrentGranularityPage]);

  /**
   * Keep currentGranularityPage in sync with freshly-fetched page data.
   *
   * A page lock is applied to ALL grains of the logical page at once, so locking/releasing
   * (or a subscription-driven refetch) changes lock attributes (locked_by/locked_at/
   * lock_expiry) on the currently-viewed grain's record without the user changing grain.
   * Since setCurrentGranularityPage short-circuits when the grain is unchanged, it does not
   * refresh the object after a refetch — so re-sync the current grain's object by pk whenever
   * pagesByName updates, so consumers (e.g. the page-lock button/banner) see live lock state.
   */
  useEffect(() => {
    if (!currentGranularityPage || !pagesByName?.length) {
      return;
    }
    const refreshedPage = pagesByName.find((page) => page.pk === currentGranularityPage.pk);
    if (refreshedPage && refreshedPage !== currentGranularityPage) {
      setCurrentGranularityPageState(refreshedPage);
    }
  }, [pagesByName, currentGranularityPage]);

  /**
   * Effect to clear Page error when Pages By Name is successfully loaded
   */
  useEffect(() => {
    if (availablePagesByName.length > 0 && pageError) {
      setPageError(null);
    }
  }, [availablePagesByName, pageError]);

  /**
   * Effect to handle API GetPagesByPageName fetch errors
   */
  useEffect(() => {
    if (isPagesByNameError && pageName) {
      setPageError(`Page with Name "${pageName}" has no page results.`);
      setCurrentGranularityPageState(null);
      setCurrentPageDriverOptionsState(null);
      setCurrentCorePageName(null);
      setModifiedPageProperties(null);
    } else if (!isPagesByNameError && pageError) {
      // Clear error when API error state changes from error to success
      setPageError(null);
    }
  }, [isPagesByNameError, pageName, pageError]);

  // Combine loading states
  const isLoading = isPagesByNameLoading;

  // Combine error states
  const isError = isPagesByNameError || !!pageError;
  const error = pageError || (isPagesByNameError ? pagesByNameError : null);

  /**
   * Creates the Page context value object
   *
   * Combines all state variables and methods into a single object that will be
   * provided to consumers of the context. This ensures that the context value
   * only changes when one of its dependencies changes, preventing unnecessary
   * re-renders of components that consume the context.
   */
  /**
   * Per-page lock state derived from the current grain page record (all grains of a
   * logical page share the same lock attributes). Editing a page requires holding THIS
   * page's lock — independent of the deck lock, which gates publish / Page Library.
   */
  /**
   * The datasets the current page is configured for — the keys of its `page_drivers` map. Centralized
   * here so the side panel and deckline name input share one derivation instead of each computing
   * `Object.keys(page_drivers)`.
   */
  const configuredDatasets = useMemo<DatasetSource[]>(
    () => Object.keys(modifiedPageProperties?.page_drivers ?? {}) as DatasetSource[],
    [modifiedPageProperties?.page_drivers],
  );

  const pageLockStatus = useMemo<LockStatus>(() => {
    const lockedBy = currentGranularityPage?.locked_by ?? undefined;
    const isLocked = Boolean(lockedBy);
    const isLockedByCurrentUser = isLocked && lockedBy === username;
    return {
      isLocked,
      lockedBy,
      lockedAt: currentGranularityPage?.locked_at ?? undefined,
      lockExpiry: currentGranularityPage?.lock_expiry ?? undefined,
      isLockedByCurrentUser,
      // Editing is disabled unless the current user holds this page's lock.
      isEditingDisabled: !isLockedByCurrentUser,
    };
  }, [currentGranularityPage, username]);

  const contextValue = useMemo<PageContextType>(
    () => ({
      availablePagesByName,
      availablePageGrains,
      currentCorePageName,
      currentGranularityPage,
      currentPageDriverOptions,
      updatePageDriverActiveState,
      setCurrentPageKPIDrivers,
      selectedGranularity,
      setSelectedGranularity,
      segmentsDisplayMode,
      setSegmentsDisplayMode,
      pageRowData,
      setPageRowData,
      isLoading,
      isError,
      error: error as Error | null,
      modifiedPageProperties,
      setDenominatorLabel,
      pageLockStatus,
      configuredDatasets,
      pageLayoutOverrides,
      setPageLayoutOverrides,
    }),
    [
      availablePagesByName,
      availablePageGrains,
      currentCorePageName,
      currentGranularityPage,
      currentPageDriverOptions,
      updatePageDriverActiveState,
      modifiedPageProperties,
      setCurrentPageKPIDrivers,
      selectedGranularity,
      segmentsDisplayMode,
      pageRowData,
      isLoading,
      isError,
      error,
      setDenominatorLabel,
      pageLockStatus,
      configuredDatasets,
      pageLayoutOverrides,
    ],
  );

  return <PageContext value={contextValue}>{children}</PageContext>;
};

/**
 * Custom hook to access the Page context
 *
 * @returns {PageContextType} The deck context value
 */
export const usePageContext = (): PageContextType => {
  return use(PageContext);
};
